import {
  JsonPlatformRestaurantAvailabilityUpdate,
  JsonPlatformAvailabilityConfig,
  JsonPlatformRestaurantAvailability,
} from '@deliveryhero/vendor-growth-api-client';
import {useHttp, useLogger} from '@deliveryhero/vendor-web-core';
import {useState, useCallback} from 'react';
import {AVAILABILITIES_CONFIG_URL, AVAILABILITIES_URL} from '../configs/availabilityUrls';
import {isAxiosErrorToFilter} from '../utils/isAxiosErrorToFilter';

type Method = 'put' | 'post' | 'get' | 'delete';
type RequestKey = 'fetchAvailabilitiesConfigs' | 'fetchAvailabilities' | 'updateAvailabilities';

type AvailabilityUpdateParams = {platformKey: string; platformRestaurantId: string};

interface RequestConfig<TResponse, TParams = undefined, TPayload = undefined> {
  key: RequestKey;
  method: Method;
  url: (params?: TParams) => string;
  params?: TParams;
  payload?: TPayload;
  response?: TResponse;
}

export type FetchAvailabilitiesConfigs = RequestConfig<JsonPlatformAvailabilityConfig[]>;
export type FetchAvailabilities = RequestConfig<JsonPlatformRestaurantAvailability[]>;
export type UpdateAvailabilities = RequestConfig<
  JsonPlatformRestaurantAvailability,
  AvailabilityUpdateParams,
  JsonPlatformRestaurantAvailabilityUpdate
>;
type AvailabilityApi = FetchAvailabilitiesConfigs | FetchAvailabilities | UpdateAvailabilities;

export const availabilityApiDict: Record<RequestKey, AvailabilityApi> = {
  fetchAvailabilitiesConfigs: <FetchAvailabilitiesConfigs>{
    key: 'fetchAvailabilitiesConfigs',
    method: 'get',
    url: (): string => AVAILABILITIES_CONFIG_URL,
  },
  fetchAvailabilities: <FetchAvailabilities>{
    key: 'fetchAvailabilities',
    method: 'get',
    url: (): string => AVAILABILITIES_URL,
  },
  updateAvailabilities: <UpdateAvailabilities>{
    key: 'updateAvailabilities',
    method: 'put',
    url: (params: AvailabilityUpdateParams): string =>
      `api/1/platforms/${params.platformKey}/restaurants/${params.platformRestaurantId}/availability`,
  },
};

interface UseApiResponse<T, K, M> {
  data: T | undefined;
  error: unknown | undefined;
  loading: boolean;
  fetchData: (params?: K, payload?: M) => Promise<T | undefined>;
}

export function useApi<T extends AvailabilityApi>(
  api: AvailabilityApi,
): UseApiResponse<T['response'], T['params'], T['payload']> {
  type TResponse = T['response'];
  type TParams = T['params'];
  type TPayload = T['payload'];

  const http = useHttp();
  const logger = useLogger();

  const [data, setData] = useState<TResponse | undefined>(undefined);
  const [error, setError] = useState<unknown | undefined>(undefined);
  const [loading, setLoading] = useState<boolean>(false);

  const fetchData = useCallback(
    async (params?: TParams, payload?: TPayload): Promise<TResponse | undefined> => {
      setLoading(true);

      let responseData: TResponse | undefined = undefined;
      try {
        const url = <(params?: TParams) => string>api.url;
        const hasPayload = !['get', 'delete'].includes(api.method);

        type RequestWithPayload = (
          url: string,
          data?: TPayload,
          config?: {headers: {Authorization: string}},
        ) => Promise<{data: TResponse}>;

        const httpMethodWithPayload = <RequestWithPayload>http[api.method].bind(http);

        const {data: respData} = hasPayload
          ? await (<Promise<{data: TResponse}>>httpMethodWithPayload(url(params), payload))
          : await (<Promise<{data: TResponse}>>http[api.method](url(params)));

        responseData = respData;
        setData(respData);
        setError(undefined);
      } catch (err: unknown) {
        if (!isAxiosErrorToFilter(err)) {
          logger.error(`useApi:${api.key}: Failed to make api call`, err, {});
        }

        setError(err);
      } finally {
        setLoading(false);
      }
      return responseData;
    },
    // TODO: useLogger and http returns new value on every rerender, that's why it's removed from depth for now
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [api],
  );

  return {data, error, loading, fetchData};
}
